/**
 * atsEngine.js — QA Resume ATS Scoring Engine
 *
 * Produces a deterministic, weighted score (0–100) with:
 *   - Keyword density analysis against a curated QA keyword bank
 *   - Missing high-value skill detection
 *   - Metrics / quantification detection in bullet points
 *   - Section completeness check (ATS-required sections)
 *   - Format compliance check (contact fields, date formats)
 *   - Actionable, prioritised suggestions
 *   - QA keyword suggestions grouped by category
 */

// ─── QA Keyword Bank ─────────────────────────────────────────────────────────

export const QA_KEYWORD_BANK = {
  testingTypes: [
    'manual testing', 'functional testing', 'regression testing', 'smoke testing',
    'sanity testing', 'integration testing', 'system testing', 'acceptance testing',
    'end-to-end testing', 'exploratory testing', 'black box testing', 'white box testing',
    'api testing', 'performance testing', 'load testing', 'stress testing',
    'security testing', 'penetration testing', 'accessibility testing', 'usability testing',
    'mobile testing', 'cross-browser testing', 'database testing', 'ui testing',
    'unit testing', 'component testing', 'contract testing', 'visual testing',
  ],
  automation: [
    'selenium', 'playwright', 'cypress', 'appium', 'webdriverio', 'puppeteer',
    'testcafe', 'robot framework', 'katalon', 'testcomplete', 'ranorex',
    'rest assured', 'postman', 'karate', 'soapui', 'insomnia',
    'jmeter', 'k6', 'gatling', 'locust', 'artillery',
    'jest', 'mocha', 'jasmine', 'pytest', 'junit', 'testng', 'nunit',
    'cucumber', 'behave', 'specflow', 'gherkin', 'bdd', 'tdd',
  ],
  aiTools: [
    'github copilot', 'cursor', 'windsurf', 'ai testing', 'ai-assisted testing',
    'prompt engineering', 'llm testing', 'generative ai', 'chatgpt', 'ai automation',
    'ai test generation', 'testim', 'mabl', 'functionize', 'applitools',
  ],
  cicd: [
    'jenkins', 'github actions', 'gitlab ci', 'azure devops', 'circleci',
    'teamcity', 'bamboo', 'travis ci', 'bitbucket pipelines', 'argocd',
    'docker', 'kubernetes', 'helm', 'terraform', 'ansible',
    'ci/cd', 'continuous integration', 'continuous delivery', 'devops',
    'shift left', 'shift-left testing', 'test automation pipeline',
  ],
  cloud: [
    'aws', 'azure', 'gcp', 'google cloud', 'browserstack', 'sauce labs',
    'lambdatest', 'perfecto', 'cloud testing', 'aws device farm',
  ],
  tools: [
    'jira', 'confluence', 'testrail', 'zephyr', 'qtest', 'practitest',
    'testlink', 'azure test plans', 'xray', 'allure', 'extent reports',
    'git', 'github', 'gitlab', 'bitbucket', 'sonarqube',
    'grafana', 'prometheus', 'datadog', 'new relic', 'splunk', 'elk stack',
    'charles proxy', 'fiddler', 'wireshark', 'burp suite',
  ],
  programming: [
    'java', 'python', 'javascript', 'typescript', 'c#', 'ruby', 'go',
    'sql', 'bash', 'shell scripting', 'powershell', 'groovy',
  ],
  methodology: [
    'agile', 'scrum', 'kanban', 'safe', 'sprint', 'test strategy',
    'test plan', 'test case', 'test suite', 'defect management',
    'bug tracking', 'root cause analysis', 'risk-based testing',
    'test coverage', 'code coverage', 'quality assurance', 'quality engineering',
    'release validation', 'test metrics', 'kpi', 'sla',
  ],
};

// Flat list for fast lookup
const ALL_QA_KEYWORDS = Object.values(QA_KEYWORD_BANK).flat();

// High-value keywords that should appear in most QA resumes
const HIGH_VALUE_KEYWORDS = [
  'selenium', 'playwright', 'cypress', 'api testing', 'automation',
  'agile', 'jira', 'ci/cd', 'regression testing', 'test strategy',
  'defect management', 'test plan', 'quality assurance',
];

// Metrics patterns — detect quantified achievements
const METRICS_PATTERNS = [
  /\d+\s*%/,                          // percentages: 85%, 40%
  /\d+x\s/i,                          // multipliers: 3x faster
  /reduced.*by\s+\d+/i,               // reduced by N
  /improved.*by\s+\d+/i,              // improved by N
  /increased.*by\s+\d+/i,             // increased by N
  /saved.*\d+\s*(hour|day|week)/i,    // saved N hours/days
  /\$\s*\d+/,                         // dollar amounts
  /\d+\s*(test|case|suite|bug|defect)/i, // N tests/bugs
  /team\s+of\s+\d+/i,                 // team of N
  /\d+\s*(project|release|sprint)/i,  // N projects/releases
];

// ─── Helpers ──────────────────────────────────────────────────────────────────

const normalise = (str) => (str || '').toLowerCase().trim();

/** Safe string helper to prevent object-to-primitive coercion crashes */
const toStr = (val) => {
  if (val === null || val === undefined) return '';
  if (typeof val === 'string') return val;
  if (typeof val === 'number' || typeof val === 'boolean') return String(val);
  if (typeof val === 'object') {
    const extracted = val.text || val.name || val.title || val.value || val.label || '';
    return typeof extracted === 'string' ? extracted : '';
  }
  return '';
};

/** Safe helper to extract raw text from both object and string achievements */
const getAchievementText = (ach) => {
  if (!ach) return '';
  if (typeof ach === 'string') return ach;
  if (typeof ach === 'object' && ach !== null) return toStr(ach.text || ach.description || ach);
  return toStr(ach);
};

/** Extract all text from formData into one searchable blob */
const extractAllText = (formData) => {
  const parts = [
    toStr(formData?.summary),
    ...(formData?.coreCompetencies || []).map(toStr),
    ...Object.values(formData?.technicalSkills || {}).flat().map(toStr),
    ...Object.values(formData?.tools || {}).flat().map(toStr),
    ...(formData?.experience || []).flatMap(e => [
      toStr(e?.role), toStr(e?.company),
      ...(e?.achievements || []).map(getAchievementText),
    ]),
    ...(formData?.projects || []).flatMap(p => [
      toStr(p?.title),
      toStr(p?.domain),
      toStr(p?.description),
      ...(Array.isArray(p?.responsibilities)
        ? p.responsibilities.map(r => (typeof r === 'string' ? r : toStr(r?.text)))
        : typeof p?.responsibilities === 'string' ? [p.responsibilities] : []),
      toStr(p?.technologies),
      toStr(p?.impact)
    ]),
    ...(formData?.certifications || []).map(c => toStr(c?.name)),
    ...(formData?.education || []).map(e => toStr(e?.degree)),
  ];
  return normalise(parts.filter(Boolean).join(' '));
};

/** All bullet-point text for metrics detection */
const extractBulletText = (formData) => [
  ...(formData?.experience || []).flatMap(e => (e?.achievements || []).map(getAchievementText)),
  ...(formData?.projects || []).flatMap(p => [
    ...(Array.isArray(p?.responsibilities)
      ? p.responsibilities.map(r => (typeof r === 'string' ? r : toStr(r?.text)))
      : typeof p?.responsibilities === 'string' ? [p.responsibilities] : []),
    toStr(p?.impact)
  ]),
  ...(formData?.achievements || []).map(a => toStr(a?.description)),
].join(' ');

// ─── Scoring Dimensions ───────────────────────────────────────────────────────

/**
 * Safely matches keywords on whole word boundaries to prevent false positives
 * (e.g. matching "go" inside "good" or "api" inside "rapidapi").
 */
export function isKeywordPresent(text, keyword) {
  if (!text || !keyword) return false;
  const escaped = keyword.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
  const pattern = `(?:^|[^a-zA-Z0-9_])${escaped}(?:$|[^a-zA-Z0-9_])`;
  const regex = new RegExp(pattern, 'i');
  return regex.test(text);
}

/**
 * KEYWORDS (35 pts)
 * Count unique QA keywords found in the resume text.
 * Cap at 35 pts. Bonus for high-value keywords.
 */
function scoreKeywords(allText) {
  const found = ALL_QA_KEYWORDS.filter(kw => isKeywordPresent(allText, kw));
  const foundSet = new Set(found);
  const highValueFound = HIGH_VALUE_KEYWORDS.filter(kw => isKeywordPresent(allText, kw));

  const base = Math.min(foundSet.size * 1.4, 28);
  const bonus = Math.min(highValueFound.length * 0.7, 7);
  const score = Math.round(Math.min(base + bonus, 35));

  const missing = HIGH_VALUE_KEYWORDS.filter(kw => !isKeywordPresent(allText, kw));

  return { score, found: [...foundSet], missing, highValueFound };
}

/**
 * SECTIONS (30 pts)
 * ATS requires specific sections to be present and non-empty.
 */
function scoreSections(formData) {
  const checks = [
    {
      label: 'Contact information (name + email)',
      pass: !!(formData?.personal?.name?.trim() && formData?.personal?.email?.trim()),
      pts: 6,
    },
    {
      label: 'Professional summary (50+ characters)',
      pass: (formData?.summary?.trim()?.length || 0) >= 50,
      pts: 5,
    },
    {
      label: 'Skills section (10+ keywords)',
      pass: [
        ...Object.values(formData?.technicalSkills || {}).flat(),
        ...Object.values(formData?.tools || {}).flat(),
        ...(formData?.coreCompetencies || []),
      ].filter(Boolean).length >= 10,
      pts: 5,
    },
    {
      label: 'Work experience with achievements',
      pass: (formData?.experience || []).some(
        e => e.company?.trim() && (e.achievements || []).some(a => getAchievementText(a).trim())
      ),
      pts: 6,
    },
    {
      label: 'At least one project',
      pass: (formData?.projects || []).some(p => p.title?.trim()),
      pts: 4,
    },
    {
      label: 'Certifications',
      pass: (formData?.certifications || []).some(c => c.name?.trim()),
      pts: 4,
    },
    {
      label: 'Education',
      pass: (formData?.education || []).some(e => e.degree?.trim() || e.university?.trim()),
      pts: 4,
    },
  ];

  const score = checks.filter(c => c.pass).reduce((sum, c) => sum + c.pts, 0);
  const missing = checks.filter(c => !c.pass).map(c => c.label);

  return { score, checks, missing };
}

/**
 * METRICS (20 pts)
 * Detect quantified achievements in bullet points.
 */
function scoreMetrics(formData) {
  const bullets = [
    ...(formData?.experience || []).flatMap(e => (e.achievements || []).map(getAchievementText)),
    ...(formData?.projects || []).map(p => p.impact || ''),
    ...(formData?.achievements || []).map(a => a.description || ''),
  ].filter(Boolean);

  const totalBullets = bullets.length;
  const bulletsWithMetrics = bullets.filter(b =>
    METRICS_PATTERNS.some(rx => rx.test(b))
  ).length;

  const ratio = totalBullets > 0 ? bulletsWithMetrics / totalBullets : 0;
  const score = Math.round(Math.min(ratio * 20 + (bulletsWithMetrics >= 3 ? 5 : 0), 20));

  return { score, bulletsWithMetrics, totalBullets };
}

/**
 * FORMAT (15 pts)
 * Contact completeness, LinkedIn, GitHub, phone, location.
 */
function scoreFormat(formData) {
  const p = formData?.personal || {};
  let score = 0;
  const missing = [];

  if (p.phone?.trim())    score += 3; else missing.push('Phone number');
  if (p.location?.trim()) score += 2; else missing.push('Location / city');
  if (p.linkedin?.trim()) score += 4; else missing.push('LinkedIn profile URL');
  if (p.github?.trim())   score += 3; else missing.push('GitHub profile URL');
  if (p.jobTitle?.trim()) score += 2; else missing.push('Job title / headline');
  if (p.portfolio?.trim()) score += 1;

  return { score: Math.min(score, 15), missing };
}

// ─── Main Export ──────────────────────────────────────────────────────────────

/**
 * computeAtsScore(formData) → AtsResult
 *
 * AtsResult {
 *   total:        number   // 0–100
 *   grade:        string   // A+ / A / B / C / D / F
 *   breakdown: {
 *     keywords:   { score, max, found, missing, highValueFound }
 *     sections:   { score, max, checks, missing }
 *     metrics:    { score, max, bulletsWithMetrics, totalBullets }
 *     format:     { score, max, missing }
 *   }
 *   suggestions:  string[] // prioritised, actionable
 *   missingKeywords: string[]  // top missing QA keywords
 *   keywordSuggestions: { [category]: string[] }  // grouped suggestions
 * }
 */
// Filter out completely empty placeholder entries so they do not impact scoring or trigger false deductions/suggestions
const pruneClientData = (formData) => {
  if (!formData) return {};
  return {
    ...formData,
    experience: (formData.experience || []).filter(e => e?.company?.trim() || e?.role?.trim()),
    projects: (formData.projects || []).filter(p => p?.title?.trim()),
    certifications: (formData.certifications || []).filter(c => c?.name?.trim()),
    education: (formData.education || []).filter(e => e?.degree?.trim() || e?.university?.trim()),
  };
};

export function computeAtsScore(formData) {
  const prunedData = pruneClientData(formData);
  const allText = extractAllText(prunedData);

  const kw  = scoreKeywords(allText);
  const sec = scoreSections(prunedData);
  const met = scoreMetrics(prunedData);
  const fmt = scoreFormat(prunedData);

  const total = Math.min(kw.score + sec.score + met.score + fmt.score, 100);

  // Grade
  const grade =
    total >= 90 ? 'A+' :
    total >= 80 ? 'A'  :
    total >= 70 ? 'B'  :
    total >= 60 ? 'C'  :
    total >= 50 ? 'D'  : 'F';

  // Build prioritised suggestions
  const suggestions = [];

  // Critical (sections missing)
  sec.missing.forEach(s => suggestions.push({ priority: 'critical', text: `Add ${s}` }));

  // High (format missing)
  fmt.missing.forEach(s => suggestions.push({ priority: 'high', text: `Add ${s}` }));

  // High (metrics)
  if (met.bulletsWithMetrics === 0) {
    suggestions.push({ priority: 'high', text: 'Add quantified achievements (e.g. "reduced bugs by 40%", "automated 300+ test cases")' });
  } else if (met.bulletsWithMetrics < 3) {
    suggestions.push({ priority: 'medium', text: `Add more metrics — only ${met.bulletsWithMetrics} of ${met.totalBullets} bullets have numbers` });
  }

  // Medium (missing high-value keywords)
  if (kw.missing.length > 0) {
    suggestions.push({
      priority: 'medium',
      text: `Add high-value QA keywords: ${kw.missing.slice(0, 5).join(', ')}`,
    });
  }

  // Low (keyword density)
  if (kw.found.length < 15) {
    suggestions.push({ priority: 'low', text: 'Expand skills section — aim for 20+ QA-specific keywords' });
  }

  // Build keyword suggestions by category (only missing ones)
  const keywordSuggestions = {};
  Object.entries(QA_KEYWORD_BANK).forEach(([cat, keywords]) => {
    const missing = keywords.filter(kw => !isKeywordPresent(allText, kw));
    if (missing.length > 0) keywordSuggestions[cat] = missing.slice(0, 6);
  });

  return {
    total,
    grade,
    breakdown: {
      keywords: { score: kw.score,  max: 35, found: kw.found, missing: kw.missing, highValueFound: kw.highValueFound },
      sections: { score: sec.score, max: 30, checks: sec.checks, missing: sec.missing },
      metrics:  { score: met.score, max: 20, bulletsWithMetrics: met.bulletsWithMetrics, totalBullets: met.totalBullets },
      format:   { score: fmt.score, max: 15, missing: fmt.missing },
    },
    suggestions: suggestions.sort((a, b) => {
      const order = { critical: 0, high: 1, medium: 2, low: 3 };
      return order[a.priority] - order[b.priority];
    }),
    missingKeywords: kw.missing,
    keywordSuggestions,
  };
}

/** Convenience: just the total score */
export const getAtsScore = (formData) => computeAtsScore(formData).total;
